/* @flow */

import { isArray, isString } from './coreutils';
import { logError } from './logger';

export const MIN_PREFIX_LEN = 3;
export const MAX_MIDDLE_LEN = 120;
// Block unbounded middle matchers in config
export const DANGEROUS_MIDDLE_RX: RegExp = /(\.\*|\.\+|\.\*\?|\.\+\?)/;

export type CompiledCleanupPattern = {
  prefix: string,
  suffix: string,
  middleRe: RegExp
};

/**
 * @param {string} middle - regex body for variable slice only (no ^ $)
 * @returns {string | null} error message or null if valid
 */
export function validateMiddlePattern(middle: string): string | null {
  if (!isString(middle) || middle.length === 0) {
    return 'middle is required';
  }
  if (middle.length > MAX_MIDDLE_LEN) {
    return `middle exceeds ${MAX_MIDDLE_LEN} characters`;
  }
  if (middle.includes('^') || middle.includes('$')) {
    return 'middle must not contain ^ or $';
  }
  if (DANGEROUS_MIDDLE_RX.test(middle)) {
    return 'middle must not contain .* or .+';
  }
  return null;
}

/**
 * @param {cleanupCookiePatternRule} raw - config rule
 * @param {number} index - rule index for logging
 * @returns {string | null} error message or null if valid
 */
export function validatePatternRule(
  // $FlowFixMe[unclear-type] unclear-type
  raw: Object,
  index: number
): string | null {
  if (!raw || typeof raw !== 'object') {
    return `rule[${index}] is not an object`;
  }
  const prefix = isString(raw.prefix) ? raw.prefix : '';
  const suffix = isString(raw.suffix) ? raw.suffix : '';
  const middle = isString(raw.middle) ? raw.middle : '';

  if (prefix.length === 0 && suffix.length === 0) {
    return `rule[${index}] requires prefix or suffix`;
  }
  if (prefix.length > 0 && prefix.length < MIN_PREFIX_LEN) {
    return `rule[${index}] prefix length must be at least ${MIN_PREFIX_LEN}`;
  }

  const middleErr = validateMiddlePattern(middle);
  if (middleErr !== null) {
    return `rule[${index}] ${middleErr}`;
  }

  return null;
}

/**
 * @param {cleanupCookiePatternRule} raw - config rule
 * @returns {?CompiledCleanupPattern} compiled rule or null if invalid
 */
export function compilePatternRule(
  // $FlowFixMe[unclear-type] unclear-type
  raw: Object
): ?CompiledCleanupPattern {
  const prefix = isString(raw.prefix) ? raw.prefix : '';
  const suffix = isString(raw.suffix) ? raw.suffix : '';
  const middle = isString(raw.middle) ? raw.middle : '';
  let middleRe;
  try {
    middleRe = new RegExp(`^${middle}$`);
  } catch {
    return null;
  }
  return { prefix, suffix, middleRe };
}

/**
 * @param {string} name - cookie name
 * @param {CompiledCleanupPattern} rule - compiled rule
 * @returns {boolean}
 */
export function nameMatchesPattern(
  name: string,
  rule: CompiledCleanupPattern
): boolean {
  const { prefix, suffix, middleRe } = rule;
  if (prefix.length > 0 && !name.startsWith(prefix)) {
    return false;
  }
  if (suffix.length > 0 && !name.endsWith(suffix)) {
    return false;
  }
  const start = prefix.length;
  const end = suffix.length > 0 ? name.length - suffix.length : name.length;
  if (start > end) {
    return false;
  }
  return middleRe.test(name.slice(start, end));
}

/**
 * @param {Array<Object>} rawRules - cleanupCookiePatterns from config
 * @returns {Array<CompiledCleanupPattern>}
 */
export function compileCleanupPatterns(
  // $FlowFixMe[unclear-type] unclear-type
  rawRules: Array<Object> = []
): Array<CompiledCleanupPattern> {
  if (!isArray(rawRules)) {
    return [];
  }
  const compiled: Array<CompiledCleanupPattern> = [];
  rawRules.forEach((raw, index) => {
    const err = validatePatternRule(raw, index);
    if (err !== null) {
      const errMsg: string = err;
      logError(`cookieCleanupPattern: ${errMsg}`);
      return;
    }
    const rule = compilePatternRule(raw);
    if (!rule) {
      logError(`cookieCleanupPattern: rule[${index}] invalid middle regex`);
      return;
    }
    compiled.push(rule);
  });
  return compiled;
}

/**
 * @param {Array<string>} names - cookie names to test
 * @param {Array<CompiledCleanupPattern>} compiled - compiled rules
 * @returns {Array<string>} names matching any rule
 */
export function findPatternMatches(
  names: Array<string>,
  compiled: Array<CompiledCleanupPattern>
): Array<string> {
  if (compiled.length === 0 || names.length === 0) {
    return [];
  }
  return names.filter(name =>
    compiled.some(rule => nameMatchesPattern(name, rule))
  );
}
